import { SupportedLanguages } from "~/providers/LanguageProvider";
import { DocumentFilter } from "~/features/Search/Results/types";
import { LabelTranslation } from "~/types";

// Search items main categories
export enum SEARCH_ITEMS {
    DISCLOSURE = "DISCLOSURE",
    CATEGORY = "CATEGORY",
    TOPIC = "TOPIC"
}

/**
 * @description Main search tab items/categories
 * - IMAGES - Graphics tab
 * - KEYWORDS - Keywords tab
 * - REPORTS - Reports tab
 * - FULL_DOCUMENTS - Full documents tab
 */
export enum SEARCH_TABS {
    IMAGES = "images",
    KEYWORDS = "texts",
    REPORTS = "pages",
    FULL_DOCUMENTS = "fullDocuments"
}

/**
 * Results viewing modes of the search pop-up modal
 */
export enum SEARCH_VIEW_MODE {
    /** 3-column selection with Disclosures, Categories and Topics */
    SELECTION = "selection",
    /** Keyword input */
    KEYWORD = "keyword"
}

/**
 * Disclosures items shape
 * @see API responses from
 * - /api/disclosures?LanguageCode=en-US
 */
export interface DisclosureItem {
    id: number;
    name: string;
    frName: string;
    count?: number;
    languageCode?: string;
}

/**
 * Metadata search items shape - list of Disclosures, Categories and Topics.
 * Response has no Topics count and no Document objects (since these are only metadata).
 * @see API responses from
 *    - /api/search/filter/image-themes
 *    - /api/search/filter/page-sections
 */
export interface DetailsResponseItem {
    DocumentTypeID: number;
    DocumentName: string;
    DocumentNameFr: string;
    CategorySections: CategoryItem[];
}

export interface CategoryItem extends Pick<DetailsResponseItem, "DocumentTypeID"> {
    CategoryID: number;
    CategoryName: string;
    CategoryNameFr: string;
    CategoryChild: TopicItem[];
}

export interface TopicItem
    extends Pick<DetailsResponseItem, "DocumentTypeID">,
        Pick<CategoryItem, "CategoryID"> {
    MetadataID: number;
    MetadataName: string;
    MetadataNameFr?: string;
}

/**
 * Normalized Disclosure | Category | Topic metadata base item definitions
 */
export interface NormalizedItem {
    /** Local array ID number */
    id: number;
    /** One of `DetailsResponseItem.DocumentTypeID`, `CategoryItem.CategoryID` or `TopicItem.MetadataID` depending on `type` */
    exploreId: number;
    /** Item type, one of `SEARCH_ITEMS` */
    type: SEARCH_ITEMS;
    /** An object containing "en" and "fr" keys with corresponding English and French text labels */
    label: LabelTranslation;
    /** Flag if item is selected */
    isSelected: boolean;
    /**
     * - Number of sub `CategoryItems` if `type === SEARCH_ITEMS.DISCLOSURE`
     * - Number of sub `TopicItems` if `type === SEARCH_ITEMS.CATEGORY`
     * - Number of documents if `type === SEARCH_ITEMS.TOPIC`
     */
    count: number;
}

export interface NormalizedDisclosure extends NormalizedItem {
    /** Number array containing selected Category IDs for this Disclosure */
    selectedCategories: number[];
}

export interface NormalizedCategory extends NormalizedItem {
    /** Disclosure ID number */
    disclosureId: number;
    /** Flag indicating if Topic counts have been fetched for this Category */
    isFetched: boolean;
    /** Number array containing selected Topic IDs for this Category */
    selectedTopicIds: number[];
}

export interface NormalizedTopic extends NormalizedItem {
    /** Disclosure ID number */
    disclosureId: number;
    /** Category ID number */
    categoryId: number;
}

export type UniqueTopicIdType = `${number}|${number}|${number}`;

export interface NormalizedCompany {
    /** Company ID */
    id: number;
    /** Company Name */
    name: string;
    /** Company Ticker */
    ticker: string;
    /** The Industry ID the company belongs to */
    industryId: number;
    /** Number array containing the Disclosure ID that the company has a document */
    disclosureIds: number[]; // multiple disclosure types?
}

export interface CompanyFilter {
    id: number;
    disclosureId: number;
    companyId: number;
}

/**
 * Solr Search
 */
export enum SolrSearchType {
    IMAGE = "imagesearch",
    PAGE = "pagesearch",
    TEXT = "textsearch",
    FULLDOCS = "fulldocsearch"
}

export type SolrSearchTypes = `${SolrSearchType}`;
export type SolrSearchRegion = `solr-${SolrSearchTypes}-${SupportedLanguages}-new`;

// Requests payload parameter types to the Solr search API
export type SolrSearchMetaKeys = "CategoryId" | "DocumentTypeID" | "RowsPerPage" | "CurrentPage" | "DocumentYear";
export type SolrMetaGraphicsParams = Pick<DocumentFilter, SolrSearchMetaKeys | "SelectedMetadata" | "DocumentYear">;
export type SolrMetaReportsParams = Pick<
    DocumentFilter,
    SolrSearchMetaKeys | "SelectedBookmarkMetadata"
>;

export type SolrMetaTopicsKeys = "SelectedMetadata" | "SelectedBookmarkMetadata";

export type SolrDynamicFilterKeys = "DocumentTypeID" | "DocumentYear" | "Industries";
export type SolrFilterParams = Pick<
    DocumentFilter,
    SolrDynamicFilterKeys | SolrMetaTopicsKeys | "SearchTerm" | "Companies" | "LanguageCode"
>;

export enum SolrTopicSearchType {
    IMAGE = "imagesearch",
    PAGE = "pagesearch"
}

export const SOLR_TOPICS_METADATA_KEY = {
    [SolrSearchType.IMAGE]: "SelectedMetadata",
    [SolrSearchType.PAGE]: "SelectedBookmarkMetadata"
};

export const SEARCH_TYPE_BY_TAB: Record<string, string> = {
    [SEARCH_TABS.IMAGES]: SolrSearchType.IMAGE,
    [SEARCH_TABS.REPORTS]: SolrSearchType.PAGE,
    [SEARCH_TABS.KEYWORDS]: SolrSearchType.TEXT,
    [SEARCH_TABS.FULL_DOCUMENTS]: SolrSearchType.FULLDOCS
};
